/**
 * Bridge Session Context
 * Manages state for the Bridge Session flow including:
 * - Current step in the flow
 * - Domain completion status
 * - Selected entropy area
 * - Active objective
 * @feature BRIDGE_SESSION_FLOW - Session state and step navigation
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  BridgeSessionState,
  BridgeSessionStep,
  DomainState,
  DomainStatus,
  EntropyHeatMapState,
  BridgeCardState,
  BridgeSessionScope,
  ScopeValidation,
} from '../types/bridgeSession';

export type BridgeSessionType = 'business' | 'product';

interface BridgeSessionContextValue {
  // State
  sessionId: string;
  sessionType: BridgeSessionType;
  currentStep: BridgeSessionStep;
  sessionState: BridgeSessionState | null;
  selectedEntropyArea: string | null;
  activeObjectiveId: string | null;
  selectedDomainId: string | null;
  selectedSubdomainId: string | null;
  scope: BridgeSessionScope | null;
  scopeValidation: ScopeValidation | null;
  
  // Methods
  setSessionState: (state: BridgeSessionState) => void;
  getDomainState: (domainId: string) => DomainStatus | null;
  getSubdomainState: (domainId: string, subdomainId: string) => import('../types/bridgeSession').SubdomainStatus | null;
  setDomainComplete: (domainId: string) => void;
  selectEntropyArea: (area: string) => void;
  navigateToStep: (step: BridgeSessionStep) => void;
  navigateToDomain: (domainId: string, subdomainId?: string) => void;
  setActiveObjective: (objectiveId: string | null) => void;
  initializeSession: (sessionId: string, initialState: BridgeSessionState, preserveStep?: BridgeSessionStep) => void;
  setScope: (scope: BridgeSessionScope | null) => void;
  setScopeValidation: (validation: ScopeValidation | null) => void;
  updateScope: (scope: BridgeSessionScope) => Promise<void>;
}

const BridgeSessionContext = createContext<BridgeSessionContextValue | undefined>(undefined);

interface BridgeSessionProviderProps {
  children: ReactNode;
  initialSessionId?: string;
  initialState?: BridgeSessionState;
  initialStep?: BridgeSessionStep;
  sessionType?: BridgeSessionType;
}

export const BridgeSessionProvider: React.FC<BridgeSessionProviderProps> = ({
  children,
  initialSessionId = '',
  initialState,
  initialStep,
  sessionType: initialSessionType = 'business',
}) => {
  const [sessionId, setSessionId] = useState<string>(initialSessionId);
  const [sessionType] = useState<BridgeSessionType>(initialSessionType);
  const [currentStep, setCurrentStep] = useState<BridgeSessionStep>(initialStep ?? 'domain_builder');
  const [sessionState, setSessionState] = useState<BridgeSessionState | null>(initialState || null);
  const [selectedEntropyArea, setSelectedEntropyArea] = useState<string | null>(null);
  const [activeObjectiveId, setActiveObjectiveId] = useState<string | null>(null);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [selectedSubdomainId, setSelectedSubdomainId] = useState<string | null>(null);
  const [scope, setScope] = useState<BridgeSessionScope | null>(initialState?.scope || null);
  const [scopeValidation, setScopeValidation] = useState<ScopeValidation | null>(initialState?.scope_validation || null);

  const initializeSession = useCallback((id: string, state: BridgeSessionState, preserveStep?: BridgeSessionStep) => {
    setSessionId(id);
    setSessionState(state);
    if (preserveStep) {
      setCurrentStep(preserveStep);
      return;
    }
    // Determine initial step based on state
    const allDomainsComplete = Object.values(state.domains).every(
      domain => domain.state === 'sufficient'
    );
    if (allDomainsComplete && state.entropyHeatMap.state === 'available') {
      setCurrentStep('entropy_heat_map');
    } else if (state.bridgeCard.state === 'complete' || state.bridgeCard.state === 'active') {
      setCurrentStep('bridge_card');
    } else {
      setCurrentStep('domain_builder');
    }
  }, []);

  const getDomainState = useCallback((domainId: string): DomainStatus | null => {
    if (!sessionState) return null;
    const domain = sessionState.domains[domainId];
    return domain || null;
  }, [sessionState]);

  const getSubdomainState = useCallback((domainId: string, subdomainId: string) => {
    if (!sessionState) return null;
    const domain = sessionState.domains[domainId];
    if (!domain) return null;
    return domain.subdomains.find(sub => sub.artifact_id === subdomainId || sub.artifact_item_id === subdomainId) || null;
  }, [sessionState]);

  const setDomainComplete = useCallback((domainId: string) => {
    if (!sessionState) return;
    
    const domain = sessionState.domains[domainId];
    if (!domain) return;
    
    const updatedState: BridgeSessionState = {
      ...sessionState,
      domains: {
        ...sessionState.domains,
        [domainId]: {
          ...domain,
          state: 'sufficient',
          completedAt: new Date().toISOString(),
        },
      },
    };
    
    // Check if all domains are complete to unlock entropy heat map
    const allDomainsComplete = Object.values(updatedState.domains).every(
      domain => domain.state === 'sufficient'
    );
    
    if (allDomainsComplete && updatedState.entropyHeatMap.state === 'locked') {
      updatedState.entropyHeatMap = { state: 'available' };
    }
    
    setSessionState(updatedState);
  }, [sessionState]);

  const selectEntropyArea = useCallback((area: string) => {
    setSelectedEntropyArea(area);
    setCurrentStep('bridge_card');
  }, []);

  const navigateToStep = useCallback((step: BridgeSessionStep) => {
    setCurrentStep(step);
  }, []);

  const navigateToDomain = useCallback((domainId: string, subdomainId?: string) => {
    setSelectedDomainId(domainId);
    setSelectedSubdomainId(subdomainId ?? null);
    setCurrentStep('domain_builder');
  }, []);

  const setActiveObjective = useCallback((objectiveId: string | null) => {
    setActiveObjectiveId(objectiveId);
    if (objectiveId && sessionState) {
      const updatedState: BridgeSessionState = {
        ...sessionState,
        bridgeCard: {
          ...sessionState.bridgeCard,
          objective_artifact_item_id: objectiveId,
          state: 'active',
        },
      };
      setSessionState(updatedState);
    }
  }, [sessionState]);

  const updateScope = useCallback(async (newScope: BridgeSessionScope) => {
    // This would typically call the API to update scope
    // For now, just update local state
    setScope(newScope);
    if (sessionState) {
      setSessionState({
        ...sessionState,
        scope: newScope,
      });
    }
  }, [sessionState]);

  const value: BridgeSessionContextValue = {
    sessionId,
    sessionType,
    currentStep,
    sessionState,
    selectedEntropyArea,
    activeObjectiveId,
    selectedDomainId,
    selectedSubdomainId,
    scope,
    scopeValidation,
    setSessionState,
    getDomainState,
    getSubdomainState,
    setDomainComplete,
    selectEntropyArea,
    navigateToStep,
    navigateToDomain,
    setActiveObjective,
    initializeSession,
    setScope,
    setScopeValidation,
    updateScope,
  };

  return (
    <BridgeSessionContext.Provider value={value}>
      {children}
    </BridgeSessionContext.Provider>
  );
};

export const useBridgeSession = (): BridgeSessionContextValue => {
  const context = useContext(BridgeSessionContext);
  if (!context) {
    throw new Error('useBridgeSession must be used within a BridgeSessionProvider');
  }
  return context;
};
